import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Mark, mergeAttributes } from '@tiptap/core';
import { Markdown } from 'tiptap-markdown';
import {
  BoldIcon,
  CodeBracketIcon,
  ItalicIcon,
  LinkIcon,
  ListBulletIcon,
  NumberedListIcon,
  PhotoIcon
} from '@heroicons/react/24/outline/index.js';

const Superscript = Mark.create({
  name: 'superscript',
  parseHTML() { return [{ tag: 'sup' }]; },
  renderHTML({ HTMLAttributes }) { return ['sup', mergeAttributes(HTMLAttributes), 0]; },
});

const Subscript = Mark.create({
  name: 'subscript',
  parseHTML() { return [{ tag: 'sub' }]; },
  renderHTML({ HTMLAttributes }) { return ['sub', mergeAttributes(HTMLAttributes), 0]; },
});

const SuperscriptIcon = () => <span className="font-bold">X<sup>2</sup></span>;
const SubscriptIcon = () => <span className="font-bold">X<sub>2</sub></span>;

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-300 bg-gray-50 p-2   ">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`cursor-pointer p-2 hover:bg-gray-200  ${
          editor.isActive('bold') ? 'bg-gray-300 ' : ''
        }`}
        title="Bold"
      >
        <BoldIcon className="size-10" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`cursor-pointer rounded p-2 hover:bg-gray-200  ${
          editor.isActive('italic') ? 'bg-gray-300 ' : ''
        }`}
        title="Italic"
      >
        <ItalicIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`cursor-pointer rounded px-3 py-2 font-semibold hover:bg-gray-200  ${
          editor.isActive('heading', { level: 2 }) ? 'bg-gray-300 ' : ''
        }`}
        title="Heading 2"
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`cursor-pointer rounded px-3 py-2 font-semibold hover:bg-gray-200  ${
          editor.isActive('heading', { level: 3 }) ? 'bg-gray-300 ' : ''
        }`}
        title="Heading 3"
      >
        H3
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`cursor-pointer rounded p-2 hover:bg-gray-200  ${
          editor.isActive('bulletList') ? 'bg-gray-300 ' : ''
        }`}
        title="Bullet List"
      >
        <ListBulletIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`rounded p-2 hover:bg-gray-200  ${
          editor.isActive('orderedList') ? 'bg-gray-300 ' : ''
        }`}
        title="Numbered List"
      >
        <NumberedListIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`cursor-pointer rounded p-2 hover:bg-gray-200  ${
          editor.isActive('codeBlock') ? 'bg-gray-300 ' : ''
        }`}
        title="Code Block"
      >
        <CodeBracketIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={addLink}
        className={`cursor-pointer rounded p-2 hover:bg-gray-200  ${
          editor.isActive('link') ? 'bg-gray-300 ' : ''
        }`}
        title="Add Link"
      >
        <LinkIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={addImage}
        className="cursor-pointer rounded p-2 hover:bg-gray-200 "
        title="Add Image"
      >
        <PhotoIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleMark('superscript').run()}
        className={`cursor-pointer rounded p-2 hover:bg-gray-200  ${
          editor.isActive('superscript') ? 'bg-gray-300 ' : ''
        }`}
        title="Superscript"
      >
        <SuperscriptIcon />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleMark('subscript').run()}
        className={`cursor-pointer rounded p-2 hover:bg-gray-200  ${
          editor.isActive('subscript') ? 'bg-gray-300 ' : ''
        }`}
        title="Subscript"
      >
        <SubscriptIcon />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`cursor-pointer rounded px-3 py-2 font-semibold hover:bg-gray-200  ${
          editor.isActive('blockquote') ? 'bg-gray-300 ' : ''
        }`}
        title="Blockquote"
      >
        &quot;
      </button>
    </div>
  );
};

export const TiptapEditor = ({ content, onChange, placeholder = 'Start writing...', invalid = false, minHeight = '200px' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Superscript,
      Subscript,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline'
        }
      }),
      Image,
      Placeholder.configure({
        placeholder
      }),
      Markdown
    ],
    content,
    onUpdate: ({ editor }) => {
      // Get markdown instead of HTML
      onChange(editor.storage.markdown.getMarkdown());
    }
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content !== editor.storage.markdown.getMarkdown()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const isAutoHeight = minHeight === 'auto';

  const editorClasses = [
    'prose prose-sm  max-w-none p-4',
    isAutoHeight ? '' : 'overflow-y-auto',
    '[&_.ProseMirror]:font-sans [&_.ProseMirror]:text-base [&_.ProseMirror]:focus:outline-none',
    '[&_.ProseMirror_a]:cursor-pointer [&_.ProseMirror_a]:text-blue-500 [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:hover:text-blue-600',
    '[&_.ProseMirror_blockquote]:ml-0 [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-gray-300 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:text-gray-600 [&_.ProseMirror_blockquote]:italic  ',
    '[&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:bg-gray-100 [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm [&_.ProseMirror_code]:text-gray-800 ',
    '[&_.ProseMirror_h2]:mt-4 [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-bold',
    '[&_.ProseMirror_h3]:mt-3 [&_.ProseMirror_h3]:mb-2 [&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h3]:font-bold',
    '[&_.ProseMirror_hr]:my-8 [&_.ProseMirror_hr]:border-0 [&_.ProseMirror_hr]:border-t-2 [&_.ProseMirror_hr]:border-gray-200 ',
    '[&_.ProseMirror_img]:my-4 [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg',
    '[&_.ProseMirror_li]:my-1',
    '[&_.ProseMirror_ol]:my-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6',
    '[&_.ProseMirror_p]:my-2',
    '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 ',
    '[&_.ProseMirror_pre]:my-4 [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:bg-slate-800 [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:text-slate-200',
    '[&_.ProseMirror_pre_code]:bg-transparent [&_.ProseMirror_pre_code]:p-0 [&_.ProseMirror_pre_code]:text-sm [&_.ProseMirror_pre_code]:text-inherit',
    '[&_.ProseMirror_ul]:my-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6'
  ].join(' ');

  return (
    <div
      className={`overflow-hidden rounded-lg border ${
        invalid ? 'border-red-500' : 'border-gray-300 '
      } bg-white `}
    >
      <MenuBar editor={editor} />
      <div
        className={editorClasses}
        style={isAutoHeight ? {} : { minHeight }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
